import { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import SearchIcon from '@mui/icons-material/Search';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import dayjs from 'dayjs';

import { BreadcrumbHeader, FilterTabs, DataTable, StatusBadge, EmptyState } from '../../components/shared';
import { shipments, partOrders } from '../../services/dataClient';
import { useAuth } from '../../auth';
import '../../styles/design-tokens.css';import ContentCard from "../../components/shared/ContentCard";

const fmtDate = (d) => {
  if (!d) return null;
  const [y, m, day] = d.split('-');
  return new Date(+y, +m - 1, +day).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// Steps per status
const STEPS = [
{ key: 'created', label: 'Shipment Created' },
{ key: 'dispatched', label: 'Dispatched' },
{ key: 'transit', label: 'In Transit' },
{ key: 'delivered', label: 'Delivered' }];

function stepsDone(status) {
  if (status === 'Delivered') return 4;
  if (status === 'In Transit') return 3;
  return 2; // Active — created + dispatched
}

function ShipmentTimelineModal({ shipment, onClose }) {
  if (!shipment) return null;
  const done = stepsDone(shipment.status);

  return (
    <Dialog
      open={Boolean(shipment)}
      onClose={(e, reason) => {if (reason !== 'backdropClick') onClose();}}
      maxWidth="xs"
      fullWidth>
      
      <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem', color: 'var(--grey-900)', pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocalShippingIcon sx={{ fontSize: 18, color: '#009688' }} />
          <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: 'var(--grey-900)' }}>
            {shipment.shipmentNumber}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: 'var(--grey-400)', '&:hover': { color: 'var(--grey-700)' }, mr: -1 }}>
          <HighlightOffIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 2.5, pb: 3, pt: '12px !important' }}>
        {/* Shipment details */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 2.5 }}>
          {[
          { label: 'Carrier', value: shipment.carrier },
          { label: 'Tracking #', value: shipment.trackingNumber },
          { label: 'Origin', value: shipment.originLocation },
          { label: 'Ticket #', value: shipment.ticketNumber ?? '—' },
          { label: 'Created', value: fmtDate(shipment.createdDate) }]
          .map(({ label, value }) =>
          <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
              <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-500)', flexShrink: 0 }}>{label}</Typography>
              <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-800)', fontWeight: 500, textAlign: 'right', wordBreak: 'break-all' }}>{value}</Typography>
            </Box>
          )}
        </Box>

        <Divider sx={{ mb: 2.5 }} />

        {/* Timeline */}
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {(() => {
            const base = dayjs(shipment.createdDate);
            const stepDates = {
              created: shipment.createdDate,
              dispatched: base.add(1, 'day').format('YYYY-MM-DD'),
              transit: base.add(2, 'day').format('YYYY-MM-DD'),
              delivered: base.add(4, 'day').format('YYYY-MM-DD')
            };
            return STEPS.map((step, i) => {
              const isComplete = i < done;
              const isLast = i === STEPS.length - 1;
              const color = isComplete ?
              i === STEPS.length - 1 ? '#2E7D32' : '#009688' :
              'var(--grey-300)';
              const date = isComplete ? fmtDate(stepDates[step.key]) : null;

              return (
                <Box key={step.key} sx={{ display: 'flex', gap: '12px' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: color, mt: '3px', flexShrink: 0 }} />
                  {!isLast &&
                    <Box sx={{ width: 2, flex: 1, backgroundColor: i < done - 1 ? color : 'var(--grey-200)', my: '3px', minHeight: 20 }} />
                    }
                </Box>
                <Box sx={{ pb: !isLast ? 2 : 0 }}>
                  <Typography sx={{ fontSize: '0.8125rem', fontWeight: isComplete ? 500 : 400, color: isComplete ? 'var(--grey-800)' : 'var(--grey-400)', lineHeight: 1.4 }}>
                    {step.label}
                  </Typography>
                  {date &&
                    <Typography sx={{ fontSize: '0.6875rem', color: 'var(--grey-400)', mt: '2px' }}>{date}</Typography>
                    }
                </Box>
              </Box>);

            });
          })()}
        </Box>
      </DialogContent>
    </Dialog>);

}

const STATUS_TABS = [
{ label: 'All', value: 'All' },
{ label: 'Active', value: 'Active' },
{ label: 'In Transit', value: 'In Transit' },
{ label: 'Delivered', value: 'Delivered' }];

export default function PortalDispatchPage() {
  const { currentUser } = useAuth();
  const [tabFilter, setTabFilter] = useState('All');
  const [search, setSearch] = useState('');
  const [sortConfig, setSortConfig] = useState({ field: 'createdDate', direction: 'desc' });
  const [activeShipment, setActiveShipment] = useState(null);

  const relevantOrderIds = useMemo(
    () => new Set(
      partOrders
      .filter((o) =>
      o.customerName === currentUser.customerName ||
      o.resellerName === currentUser.customerName
      )
      .map((o) => o.id)
    ),
    [currentUser.customerName]
  );

  const myShipments = useMemo(
    () => shipments.filter((s) =>
      s.customerName === currentUser.customerName ||
      (s.partOrderId && relevantOrderIds.has(s.partOrderId))
    ),
    [currentUser.customerName, relevantOrderIds]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = tabFilter === 'All' ? myShipments : myShipments.filter((s) => s.status === tabFilter);
    const searched = !q ? base : base.filter((s) =>
    s.shipmentNumber.toLowerCase().includes(q) ||
    (s.ticketNumber ?? '').toLowerCase().includes(q) ||
    s.carrier.toLowerCase().includes(q) ||
    s.trackingNumber.toLowerCase().includes(q)
    );
    return [...searched].sort((a, b) => {
      const av = a[sortConfig.field] ?? '';
      const bv = b[sortConfig.field] ?? '';
      const cmp = String(av).localeCompare(String(bv));
      return sortConfig.direction === 'asc' ? cmp : -cmp;
    });
  }, [myShipments, tabFilter, search, sortConfig]);

  const handleSort = (field) =>
  setSortConfig((prev) => ({ field, direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc' }));

  const columns = [
  {
    field: 'shipmentNumber',
    headerName: 'Shipment #',
    sortable: true,
    renderCell: (row) =>
    <Typography
      onClick={(e) => {e.stopPropagation();setActiveShipment(row);}}
      sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#009688', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
      
          {row.shipmentNumber}
        </Typography>

  },
  { field: 'ticketNumber', headerName: 'Ticket #', sortable: true },
  { field: 'carrier', headerName: 'Carrier', sortable: true },
  {
    field: 'trackingNumber',
    headerName: 'Tracking #',
    sortable: false,
    renderCell: (row) =>
    <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-600)', fontFamily: 'monospace' }}>
          {row.trackingNumber}
        </Typography>

  },
  {
    field: 'status',
    headerName: 'Status',
    sortable: true,
    renderCell: (row) => <StatusBadge status={row.status} />
  },
  { field: 'createdDate', headerName: 'Created', sortable: true }];

  return (
    <Box sx={{ pt: 0, px: 3, pb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <ShipmentTimelineModal shipment={activeShipment} onClose={() => setActiveShipment(null)} />
      <BreadcrumbHeader
        title="My Shipments"
        crumbs={[{ label: 'Portal' }, { label: 'My Shipments' }]} />

      <ContentCard sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <FilterTabs tabs={STATUS_TABS} value={tabFilter} onChange={setTabFilter} />

        <TextField
          fullWidth
          placeholder="Search by Shipment #, Ticket #, Carrier, or Tracking #…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 20, color: 'var(--grey-400)' }} /></InputAdornment> }}
          sx={{ '& .MuiOutlinedInput-root': {
              fontSize: '0.875rem',
              '& fieldset': { borderColor: 'var(--grey-200)' },
              '&:hover fieldset': { borderColor: 'var(--grey-400)' },
              '&.Mui-focused fieldset': { borderColor: '#009688' }
            }
          }} />

        <Box sx={{ border: '1px solid var(--grey-200)', borderRadius: '8px', overflow: 'hidden' }}>
          {filtered.length === 0 ?
          <Box sx={{ p: 3 }}>
              <EmptyState title="No shipments found" description="No shipments match the selected filter." />
            </Box> :

          <DataTable columns={columns} data={filtered} sortConfig={sortConfig} onSort={handleSort} />
          }
        </Box>
      </ContentCard>
    </Box>);

}